import React, { useState, useMemo, useEffect } from 'react'
import { Skeleton, Result, Icon, Pagination, message } from 'antd'
import { CSSTransition } from 'react-transition-group'
import { useTranslation, Trans } from 'react-i18next'
import AdsSectionHeader from './AdsSectionHeader'
import AdsToolbar from './AdsToolbar'
import AdsFiltersPanel from './AdsFiltersPanel'
import FoldersGroup from './FoldersGroup'
import AdGrid from './AdGrid'
import { AdsContentSkeleton, AdsGridSkeleton } from './AdLibrarySkeletons'
import BulkActionBar from './BulkActionBar'
import useMergedDropzoneRef from '../utils/useMergedDropzoneRef'
import { ACCEPT, MAX_SIZE_MB } from '../utils/fileValidation'
import { findFolderPath } from '../utils/folderTree'
import AdsSectionHolder from '../styles/adsSectionStyle'
// The backend's catch-all "Por defecto" tag. It is not shown as a folder in the
// Ad Library (it's the default bucket, not a user-created folder).
const DEFAULT_FOLDER_ID = 1

// Server-side ads page size (must match PAGE_SIZE in AdLibrarySagas).
const PAGE_SIZE = 24

const AdsSection = ({
  folders = [],
  ads = [],
  adsTotal = 0,
  page = 1,
  onPageChange,
  brands = [],
  selectedTag,
  canBrowse = false,
  loadingAds = false,
  loadingFolders = false,
  uploading = false,
  onSearch,
  onTypeChange,
  sort,
  onSortChange,
  onUploadFiles,
  selectedIds = [],
  onToggleSelect,
  onSetSelection,
  onClearSelection,
  onDeleteSelected,
  onSelectFolder,
  openModal,
}) => {
  const { t } = useTranslation()
  const [viewMode, setViewMode] = useState('grid')
  const [filtersOpen, setFiltersOpen] = useState(true)
  const [filters, setFilters] = useState({ type: null, status: null })
  // Folder selection lives here so the bulk bar can combine it with ad selection.
  const [selectedFolderIds, setSelectedFolderIds] = useState([])
  // Id of the folder currently under a file drag (read from the drag event
  // target). Drives the folder highlight and suppresses the section overlay so
  // it doesn't mask the targeted folder. null => dragging over the ads area.
  const [dragFolderId, setDragFolderId] = useState(null)

  // Navigating to another folder swaps both folders and ads, so reset every
  // selection to keep the combined count accurate.
  useEffect(() => {
    setSelectedFolderIds([])
    if (onClearSelection) onClearSelection()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTag])

  // The "Por defecto" tag is the backend's catch-all bucket, not a real folder.
  // Top-level folders shown at the root of the navigation.
  const rootFolders = useMemo(
    () => folders.filter((f) => String(f.id) !== String(DEFAULT_FOLDER_ID)),
    [folders],
  )

  // Navigation path (root → current folder) derived from the selected tag.
  const path = useMemo(
    () => (selectedTag != null ? findFolderPath(folders, selectedTag) : []),
    [folders, selectedTag],
  )
  const currentFolder = path.length ? path[path.length - 1] : null

  // Folders shown at the current level: the current folder's subfolders, or the
  // top-level folders when at the root.
  const displayedFolders = useMemo(
    () => (currentFolder ? currentFolder.children || [] : rootFolders),
    [currentFolder, rootFolders],
  )

  // After a bulk folder delete (the tree refetches, same tag) prune any selected
  // ids that no longer exist, so the bulk bar count doesn't count deleted folders.
  useEffect(() => {
    setSelectedFolderIds((prev) => {
      const next = prev.filter((id) => displayedFolders.some((f) => f.id === id))
      return next.length === prev.length ? prev : next
    })
  }, [displayedFolders])

  // --- Drag & drop upload (react-dropzone) --------------------------------
  // A SINGLE drop zone wraps the whole ads area. Folders aren't separate drop
  // zones — they just carry a `data-folder-drop`/`data-folder-id` marker, and we
  // resolve the target from the drag event: dropping on a folder uploads into it,
  // dropping anywhere else uploads into the folder we're standing in.
  const folderIdAt = (e) => {
    const el = e.target.closest && e.target.closest('[data-folder-drop]')
    return el && el.getAttribute('data-folder-id') ? Number(el.getAttribute('data-folder-id')) : null
  }

  const { rootProps: dropzoneRootProps, mergedRef, isDragActive } = useMergedDropzoneRef({
    noClick: true,
    noKeyboard: true,
    multiple: true,
    accept: ACCEPT,
    maxSize: MAX_SIZE_MB * 1024 * 1024,
    disabled: !onUploadFiles || uploading,
    // react-dropzone validates type/size against accept/maxSize; rejected files
    // land in `rejections`. The third arg is the drop event — its target tells
    // us which folder (if any) the files were dropped on.
    onDrop: (accepted, rejections, event) => {
      setDragFolderId(null)
      if (rejections.length) message.error(t('Supports JPG, PNG, MP4, MP3 (Max 500MB)'))
      if (!accepted.length) return
      onUploadFiles(accepted, folderIdAt(event) || undefined)
    },
  })

  // Compose our own drag handlers onto react-dropzone's root: track which folder
  // the pointer is over (for the highlight + overlay suppression). react-dropzone
  // doesn't stop drag-event propagation, so these fire for folder children too.
  const rootProps = {
    ...dropzoneRootProps,
    className: `ads-dropzone${isDragActive && dragFolderId === null ? ' drag-over' : ''}`,
    onDragOver: (e) => {
      if (dropzoneRootProps.onDragOver) dropzoneRootProps.onDragOver(e)
      if (isDragActive) setDragFolderId(folderIdAt(e))
    },
    onDragLeave: (e) => {
      if (dropzoneRootProps.onDragLeave) dropzoneRootProps.onDragLeave(e)
      if (!e.currentTarget.contains(e.relatedTarget)) setDragFolderId(null)
    },
  }

  // Where a section-level drop will land (shown in the overlay hint).
  const dropDestination = currentFolder ? currentFolder.name : t('My ads')

  // Up to two brand names used as examples in the "select a folder" placeholder.
  const exampleBrands = brands.slice(0, 2).map((b) => b.name)

  // Folders and ads load in parallel; keep BOTH in skeleton state until BOTH
  // finish, so the content appears in a single transition (no two-step reflow
  // where folders pop in before ads).
  const loadingContent = loadingFolders || loadingAds

  // The combined skeleton is for the FIRST load only. Afterwards the folder tree
  // stays in memory, so later ad reloads (folder navigation, server-side search)
  // show the ad skeleton alone and keep the folders visible.
  const [hasLoadedContent, setHasLoadedContent] = useState(false)
  useEffect(() => {
    if (canBrowse && !loadingContent) setHasLoadedContent(true)
  }, [canBrowse, loadingContent])

  // Search, type and sort are server-side (the `search` / `type` / `sort`
  // params); only status stays client-side.
  const visibleAds = useMemo(() => {
    if (!filters.status) return ads
    return ads.filter((a) => a.status === filters.status)
  }, [ads, filters])

  const getAdMenuItems = (ad) => [
    { key: 'preview', label: t('Preview'), onClick: () => openModal('previewAd', { ad }) },
    { key: 'rename', label: t('Rename'), onClick: () => openModal('renameAd', { ad }) },
    { key: 'delete', label: t('Archive'), onClick: () => openModal('deleteAds', { ids: [ad.id] }) },
  ]

  // Open the full preview for an ad (thumbnail click or the "Preview" menu item).
  const handlePreview = (ad) => openModal('previewAd', { ad })

  // Apply: the type filter goes server-side (lifts to the parent's getContents);
  // status stays client-side. Keep both in `filters` so the panel shows them.
  const handleApplyFilters = (next) => {
    setFilters(next)
    if (onTypeChange) onTypeChange(next.type || null)
  }

  const handleClearAll = () => {
    if (onClearSelection) onClearSelection()
    setSelectedFolderIds([])
  }

  // Bulk delete acts on the whole selection — ads and/or folders. The parent
  // opens a single confirm and dispatches each kind to its own endpoint.
  const handleDeleteSelected = () => {
    if ((selectedIds.length || selectedFolderIds.length) && onDeleteSelected) {
      onDeleteSelected(selectedIds, selectedFolderIds)
    }
  }

  return (
    <AdsSectionHolder>
      <AdsSectionHeader
        path={path}
        onNavigate={onSelectFolder}
        uploading={uploading}
        onUploadAd={() => openModal('uploadAd')}
        onCreateFolder={() => openModal('createFolder')}
      />
      {/* Toolbar + filters are always present (independent of folders/ads). */}
      <AdsToolbar
        onSearch={onSearch}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        sort={sort}
        onSortChange={onSortChange}
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((open) => !open)}
      />


      <CSSTransition in={filtersOpen} timeout={250} classNames="filters-panel" unmountOnExit>
        <AdsFiltersPanel filters={filters} onApply={handleApplyFilters} />
      </CSSTransition>

      {!canBrowse ? (
        <Result
          className="empty-folder-result"
          icon={<Icon type="folder-open"  theme="twoTone" />}
          title={t('Select a brand')}
          extra={
            exampleBrands.length >= 2 ? (
              <Trans
                i18nKey="Choose one of the folders above (for example, {{first}} or {{second}}) to view and manage its ads."
                values={{ first: exampleBrands[0], second: exampleBrands[1] }}
                components={{ strong: <strong /> }}
              />
            ) : exampleBrands.length === 1 ? (
              <Trans
                i18nKey="Choose one of the folders above (for example, {{first}}) to view and manage its ads."
                values={{ first: exampleBrands[0] }}
                components={{ strong: <strong /> }}
              />
            ) : (
              t('Choose one of the folders above to view and manage its ads.')
            )
          }
        />
      ) : (
        <>
          {/* Single sticky bulk bar combining selected ads + folders */}
          <BulkActionBar
            count={selectedIds.length + selectedFolderIds.length}
            onClear={handleClearAll}
            onDelete={handleDeleteSelected}
            deleteDisabled={selectedIds.length === 0 && selectedFolderIds.length === 0}
          />

          {/* Drop zone: dropping files here uploads to the current folder;
              dropping onto a folder card/row uploads into that folder. */}
          <div ref={mergedRef} {...rootProps}>
            {isDragActive && dragFolderId === null && (
              <div className="ads-dropzone-overlay">
                <Icon type="cloud-upload" className="ads-dropzone-icon" />
                <span className="ads-dropzone-title">{t('Drop files to upload')}</span>
                <span className="ads-dropzone-hint">
                  <Trans
                    i18nKey="They will be saved in {{folder}}"
                    values={{ folder: dropDestination }}
                    components={{ strong: <strong /> }}
                  />
                </span>
              </div>
            )}

            {!hasLoadedContent && loadingContent ? (
              /* First load: one combined skeleton (folders + ads) so the content
                 lands in a single transition. */
              <AdsContentSkeleton viewMode={viewMode} />
            ) : (
              <>
                {/* Folders below the filters */}
                <FoldersGroup
                  folders={displayedFolders}
                  viewMode={viewMode}
                  selectedIds={selectedFolderIds}
                  onSelectionChange={setSelectedFolderIds}
                  onSelectFolder={onSelectFolder}
                  dragFolderId={onUploadFiles ? dragFolderId : null}
                  openModal={openModal}
                />

              {/* Ads reload on navigation/search without touching the folders, so
                  only this area shows the skeleton after the first load. */}
              <div className="ads-group">
                {loadingAds ? (
                  viewMode === 'list' ? (
                    <Skeleton active title={false} paragraph={{ rows: 6 }} />
                  ) : (
                    <AdsGridSkeleton />
                  )
                ) : (
                  <AdGrid
                    ads={visibleAds}
                    viewMode={viewMode}
                    selectedIds={selectedIds}
                    onToggleSelect={onToggleSelect}
                    onSetSelection={onSetSelection}
                    getMenuItems={getAdMenuItems}
                    onPreview={handlePreview}
                  />
                )}
              </div>

              {/* Server-side ads pagination (24/page). Folders are never paginated.
                  Kept mounted while a page loads so it doesn't flicker. */}
              {adsTotal > 0 && (
                <Pagination
                  className="ads-pagination"
                  current={page}
                  pageSize={PAGE_SIZE}
                  total={adsTotal}
                  onChange={onPageChange}
                  showSizeChanger={false}
                />
              )}
            </>
            )}
          </div>
        </>
      )}
    </AdsSectionHolder>
  )
}

export default AdsSection
